import { ViewIds } from '@ardoq/api-types';
import { ViewLegendContainerElement } from '@ardoq/graph';
import {
  EXPORT_FORMAT,
  type ExportLabels,
  type RecordedSvgOptions,
  deliverExport,
} from '@ardoq/export';
import { getStyleSheets } from 'tabview/getSharedExportFunctions';
import { runViewExport } from 'tabview/runViewExport';

type RenderSvgDocument = (
  options: RecordedSvgOptions
) => Promise<string | null>;

type SvgExporterArgs = {
  getExportContainer: () => HTMLElement | null;
  renderSvgDocument: RenderSvgDocument;
  viewId: ViewIds;
  isViewpointMode: boolean;
  labels: ExportLabels;
};

/**
 * Saves an SVG document produced by a canvas-rendering view, the SVG counterpart
 * of `pngExporter`: the view renders the document, and this places it in a file.
 */
export const svgExporter =
  ({
    getExportContainer,
    renderSvgDocument,
    viewId,
    isViewpointMode,
    labels,
  }: SvgExporterArgs) =>
  () =>
    runViewExport({
      viewId,
      format: EXPORT_FORMAT.SVG,
      isViewpointMode,
      labels,
      run: async ({ transaction, mutableTrackingData }) => {
        const viewLegend = getExportContainer()?.querySelector(
          ViewLegendContainerElement.toString()
        );

        const svg = await renderSvgDocument({
          legendElement:
            viewLegend instanceof HTMLElement ? viewLegend : undefined,
          title: viewId,
          getStyleSheets,
          transaction,
          mutableTrackingData,
        });

        if (!svg) {
          throw Error('SVG export produced no document');
        }

        deliverExport(
          new Blob([svg], { type: 'image/svg+xml' }),
          `${viewId}.svg`
        );
      },
    });
